/**
 * PatientValueCard — box "Valore Opportunità Paziente" nella Dashboard.
 *
 * Aggrega le opportunità per PAZIENTE mostrando somme € aperte/vinte in
 * una finestra temporale. Filtri:
 *  - Assicurazione (default: tutti gli assicurati, escluso BDD self-pay)
 *  - Mesi (multi-select)
 *  - Status (open/won multi-select)
 *  - Fasce di valore (7 buckets: 0-200, 201-500, ..., oltre-10001)
 *
 * Endpoint: GET /api/dashboard/patient-value
 *
 * Ordinata per valore combinato € DESC. Mostra:
 *  - Banner totali (Pazienti, € Aperte, € Vinte, € Totale)
 *  - Distribuzione visiva per fascia (barre orizzontali)
 *  - Tabella pazienti con dettaglio opp singole (expandable)
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import {
  Wallet, ChevronDown, X as XIcon, Download, Users, ChevronRight,
} from "lucide-react";
import { cachedGet } from "@/lib/apiCache";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTH_IT_LONG = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

// Devono coincidere con VALUE_BUCKETS nel backend (routes_dashboard.py).
const VALUE_BUCKETS = [
  { key: "0-200",       label: "€ 0 – 200" },
  { key: "201-500",     label: "€ 201 – 500" },
  { key: "501-1000",    label: "€ 501 – 1.000" },
  { key: "1001-3000",   label: "€ 1.001 – 3.000" },
  { key: "3001-5000",   label: "€ 3.001 – 5.000" },
  { key: "5001-10000",  label: "€ 5.001 – 10.000" },
  { key: "oltre-10001", label: "€ oltre 10.001" },
];

const STATUS_OPTIONS = [
  { key: "open", label: "Aperte" },
  { key: "won", label: "Vinte" },
];

function buildMonthOptions() {
  const now = new Date();
  const opts = [];
  let y = now.getFullYear();
  let m = now.getMonth();
  for (let i = 0; i < 36; i += 1) {
    opts.push({ ym: `${y}-${String(m + 1).padStart(2, "0")}`, label: `${MONTH_IT_LONG[m]} ${y}` });
    if (m === 0) { y -= 1; m = 11; } else { m -= 1; }
  }
  return opts;
}

function euro(v) {
  return `€ ${(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Dropdown multi-select con chip-summary + outside-click close.
 * Riusato dallo stesso pattern di PatientSearchCard.jsx per coerenza UX.
 */
function MultiSelect({ label, options, values, onChange, placeholder, testid, disabled }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggle = (val) => {
    if (values.includes(val)) onChange(values.filter((v) => v !== val));
    else onChange([...values, val]);
  };

  return (
    <div className="relative" data-testid={testid} ref={containerRef}>
      {label && <label className="block text-[11px] font-semibold text-slate-500 mb-1">{label}</label>}
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className={`h-10 w-full text-sm border rounded-lg px-3 bg-white text-left flex items-center justify-between gap-2 ${disabled ? "border-slate-100 text-slate-400" : "border-slate-200 hover:border-slate-300"}`}
      >
        <span className="truncate">
          {values.length === 0 ? (
            <span className="text-slate-400">{placeholder || "Seleziona…"}</span>
          ) : (
            <span className="text-slate-700">
              {values.length} {values.length === 1 ? "selezionato" : "selezionati"}
            </span>
          )}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 z-30 bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-auto">
          {options.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-400">Nessuna opzione</div>
          )}
          {options.map((o) => {
            const val = typeof o === "string" ? o : o.value;
            const lbl = typeof o === "string" ? o : o.label;
            const checked = values.includes(val);
            return (
              <button
                key={val}
                type="button"
                onClick={() => toggle(val)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${checked ? "bg-emerald-50 text-emerald-700" : "text-slate-700"}`}
              >
                <span className={`w-4 h-4 border rounded-sm flex items-center justify-center ${checked ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`}>
                  {checked && <span className="text-white text-[10px] leading-none">✓</span>}
                </span>
                <span className="truncate">{lbl}</span>
              </button>
            );
          })}
          {values.length > 0 && (
            <div className="sticky bottom-0 border-t border-slate-100 px-3 py-2 flex justify-between items-center bg-white">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-[11px] text-rose-600 hover:underline"
              >
                Deseleziona tutti
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[11px] text-emerald-700 hover:underline"
              >
                Chiudi
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PatientValueCard() {
  const [insurance, setInsurance] = useState("");
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(["open", "won"]);
  const [selectedBuckets, setSelectedBuckets] = useState([]);
  const [insurances, setInsurances] = useState([]);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({}); // contact_id → bool

  const monthOptions = useMemo(() => buildMonthOptions(), []);

  useEffect(() => {
    cachedGet(`${API}/dashboard/insurances-list`)
      .then(({ data }) => setInsurances(data?.insurances || []))
      .catch(() => setInsurances([]));
  }, []);

  const runSearch = useCallback(async () => {
    setSearching(true);
    setError("");
    try {
      const params = {};
      if (insurance) params.insurance = insurance;
      if (selectedMonths.length) params.months = selectedMonths.join(",");
      if (selectedStatuses.length) params.statuses = selectedStatuses.join(",");
      if (selectedBuckets.length) params.value_buckets = selectedBuckets.join(",");
      const { data } = await axios.get(`${API}/dashboard/patient-value`, {
        params, withCredentials: true,
      });
      setResults(data);
      setExpanded({});
    } catch (e) {
      setError(e?.response?.data?.detail || "Impossibile eseguire la ricerca");
      setResults(null);
    } finally {
      setSearching(false);
    }
  }, [insurance, selectedMonths, selectedStatuses, selectedBuckets]);

  const resetAll = () => {
    setInsurance("");
    setSelectedMonths([]);
    setSelectedStatuses(["open", "won"]);
    setSelectedBuckets([]);
    setResults(null);
    setError("");
    setExpanded({});
  };

  const exportCsv = useCallback(() => {
    if (!results?.patients?.length) return;
    const rows = [];
    rows.push(["Paziente", "Contact ID", "Assicurazione", "Fascia €",
      "Opp Aperte", "€ Aperte", "Opp Vinte", "€ Vinte", "€ Totale"]);
    results.patients.forEach((p) => {
      rows.push([
        `"${(p.contact_name || "").replace(/"/g, '""')}"`,
        p.contact_id,
        `"${(p.insurance || "").replace(/"/g, '""')}"`,
        p.bucket,
        p.count_open, p.total_open_euro,
        p.count_won, p.total_won_euro,
        p.total_combined_euro,
      ]);
    });
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `valore-pazienti-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [results]);

  const maxBucketPatients = useMemo(() => {
    if (!results?.distribution_by_bucket) return 1;
    return Math.max(1, ...results.distribution_by_bucket.map((d) => d.patients));
  }, [results]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5" data-testid="patient-value-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <Wallet className="w-4 h-4 text-emerald-500" />
          Valore Opportunità Paziente
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Assicurazione */}
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 mb-1">Assicurazione</label>
          <select
            value={insurance}
            onChange={(e) => setInsurance(e.target.value)}
            data-testid="value-insurance-select"
            className="h-10 w-full text-sm border border-slate-200 rounded-lg px-3 bg-white"
          >
            <option value="">Tutti gli assicurati (no BDD)</option>
            <option value="all">Tutti (incluso BDD self-pay)</option>
            {insurances.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>

        <MultiSelect
          label="Mesi"
          options={monthOptions.map((m) => ({ value: m.ym, label: m.label }))}
          values={selectedMonths}
          onChange={setSelectedMonths}
          placeholder="Tutti i mesi"
          testid="value-months-multi"
        />

        <MultiSelect
          label="Stato opportunità"
          options={STATUS_OPTIONS.map((s) => ({ value: s.key, label: s.label }))}
          values={selectedStatuses}
          onChange={setSelectedStatuses}
          placeholder="Aperte + Vinte"
          testid="value-statuses-multi"
        />

        <MultiSelect
          label="Fascia valore €"
          options={VALUE_BUCKETS.map((b) => ({ value: b.key, label: b.label }))}
          values={selectedBuckets}
          onChange={setSelectedBuckets}
          placeholder="Tutte le fasce"
          testid="value-buckets-multi"
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={runSearch}
          disabled={searching || selectedStatuses.length === 0}
          data-testid="value-submit"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
          title={selectedStatuses.length === 0 ? "Seleziona almeno uno stato" : ""}
        >
          <Wallet className="w-4 h-4" />
          {searching ? "Ricerca…" : "Calcola"}
        </button>
        <button
          type="button"
          onClick={resetAll}
          data-testid="value-reset"
          className="inline-flex items-center gap-1 h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm"
        >
          <XIcon className="w-4 h-4" /> Reset
        </button>
        {results?.patients?.length > 0 && (
          <button
            type="button"
            onClick={exportCsv}
            data-testid="value-export-csv"
            className="ml-auto inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm"
          >
            <Download className="w-4 h-4" /> Esporta CSV
          </button>
        )}
      </div>

      {error && (
        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {error}
        </div>
      )}

      {results && (
        <div className="mt-5 border-t border-slate-100 pt-4">
          {/* Banner totali */}
          <div
            className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-3"
            data-testid="value-summary"
          >
            <SummaryBox
              icon={<Users className="w-4 h-4" />}
              label="Pazienti trovati"
              value={results.total_patients.toLocaleString("it-IT")}
              color="emerald"
              testid="value-total-patients"
            />
            <SummaryBox
              label="€ Aperte"
              value={euro(results.grand_total_open_euro)}
              color="amber"
              testid="value-total-open"
            />
            <SummaryBox
              label="€ Vinte"
              value={euro(results.grand_total_won_euro)}
              color="blue"
              testid="value-total-won"
            />
            <SummaryBox
              label="€ Totale"
              value={euro(results.grand_total_combined_euro)}
              color="emerald"
              highlight
              testid="value-total-combined"
            />
          </div>

          {results.patients.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-400" data-testid="value-empty">
              Nessun paziente trovato con i filtri selezionati.
            </div>
          ) : (
            <>
              {/* Distribuzione per fascia */}
              <div className="mb-4">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
                  Distribuzione per fascia di valore
                </div>
                <div className="space-y-1.5" data-testid="value-distribution">
                  {results.distribution_by_bucket.map((d) => {
                    const bucketMeta = VALUE_BUCKETS.find((b) => b.key === d.bucket);
                    const pct = maxBucketPatients ? (d.patients / maxBucketPatients) * 100 : 0;
                    return (
                      <div key={d.bucket} className="flex items-center gap-3 text-xs">
                        <div className="w-32 text-slate-600 font-medium">{bucketMeta?.label || d.bucket}</div>
                        <div className="flex-1 h-6 bg-slate-100 rounded-md overflow-hidden relative">
                          <div
                            className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all"
                            style={{ width: `${pct}%` }}
                          />
                          {d.patients > 0 && (
                            <div className="absolute inset-0 flex items-center px-2 text-[10px] font-semibold text-emerald-900">
                              {d.patients} paz. · {euro(d.total_euro)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Tabella pazienti */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="value-results-table">
                  <thead>
                    <tr className="text-slate-500 border-b border-slate-200 text-[10px] uppercase tracking-wide">
                      <th className="w-6"></th>
                      <th className="text-left py-2 px-2 font-semibold">Paziente</th>
                      <th className="text-left py-2 px-2 font-semibold">Assicurazione</th>
                      <th className="text-right py-2 px-2 font-semibold">Aperte</th>
                      <th className="text-right py-2 px-2 font-semibold">€ Aperte</th>
                      <th className="text-right py-2 px-2 font-semibold">Vinte</th>
                      <th className="text-right py-2 px-2 font-semibold">€ Vinte</th>
                      <th className="text-right py-2 px-2 font-semibold">€ Totale</th>
                      <th className="text-left py-2 px-2 font-semibold">Fascia</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {results.patients.map((p) => {
                      const isOpen = !!expanded[p.contact_id];
                      const bucketMeta = VALUE_BUCKETS.find((b) => b.key === p.bucket);
                      return (
                        <>
                          <tr
                            key={p.contact_id}
                            className="hover:bg-slate-50 cursor-pointer"
                            onClick={() => setExpanded((prev) => ({ ...prev, [p.contact_id]: !isOpen }))}
                            data-testid={`value-row-${p.contact_id}`}
                          >
                            <td className="text-slate-400 px-1">
                              <ChevronRight className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                            </td>
                            <td className="py-2 px-2 font-medium text-slate-800">{p.contact_name}</td>
                            <td className="py-2 px-2 text-slate-600">{p.insurance}</td>
                            <td className="py-2 px-2 text-right text-slate-700 tabular-nums">{p.count_open}</td>
                            <td className="py-2 px-2 text-right text-amber-700 tabular-nums font-semibold">
                              {p.total_open_euro > 0 ? euro(p.total_open_euro) : "—"}
                            </td>
                            <td className="py-2 px-2 text-right text-slate-700 tabular-nums">{p.count_won}</td>
                            <td className="py-2 px-2 text-right text-blue-700 tabular-nums font-semibold">
                              {p.total_won_euro > 0 ? euro(p.total_won_euro) : "—"}
                            </td>
                            <td className="py-2 px-2 text-right text-emerald-700 tabular-nums font-bold">
                              {euro(p.total_combined_euro)}
                            </td>
                            <td className="py-2 px-2 text-slate-500 text-[10px] whitespace-nowrap">
                              {bucketMeta?.label || p.bucket}
                            </td>
                          </tr>
                          {isOpen && (
                            <tr>
                              <td colSpan={9} className="bg-slate-50 px-4 py-2">
                                <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">
                                  Opportunità di {p.contact_name} ({p.opportunities.length})
                                </div>
                                <table className="w-full text-[11px]">
                                  <thead className="text-slate-400">
                                    <tr>
                                      <th className="text-left px-1 py-1 font-medium">Nome</th>
                                      <th className="text-left px-1 py-1 font-medium">Pipeline / Stage</th>
                                      <th className="text-left px-1 py-1 font-medium">Stato</th>
                                      <th className="text-left px-1 py-1 font-medium">Data piano</th>
                                      <th className="text-right px-1 py-1 font-medium">€</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {p.opportunities.map((op) => (
                                      <tr key={op.opp_id} className="border-t border-slate-200/50">
                                        <td className="px-1 py-1 text-slate-700 truncate max-w-[220px]" title={op.opp_name}>{op.opp_name}</td>
                                        <td className="px-1 py-1 text-slate-500">
                                          <span className="text-[9px] text-slate-400 block">{op.pipeline_name}</span>
                                          {op.stage_name}
                                        </td>
                                        <td className="px-1 py-1">
                                          <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold ${
                                            op.status === "won" ? "bg-blue-100 text-blue-700" :
                                            op.status === "open" ? "bg-amber-100 text-amber-700" :
                                            "bg-slate-100 text-slate-600"
                                          }`}>
                                            {op.status}
                                          </span>
                                        </td>
                                        <td className="px-1 py-1 text-slate-500 tabular-nums whitespace-nowrap">
                                          {op.data_piano_cura?.slice(0, 10) || "—"}
                                        </td>
                                        <td className="px-1 py-1 text-right text-slate-800 tabular-nums font-semibold">
                                          {euro(op.total_euro)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryBox({ icon, label, value, color = "slate", highlight = false, testid }) {
  const colorMap = {
    emerald: highlight
      ? "border-emerald-300 bg-gradient-to-br from-emerald-50 to-emerald-100 text-emerald-800"
      : "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
  };
  return (
    <div
      className={`rounded-xl border-2 ${colorMap[color] || colorMap.slate} px-3 py-2.5 ${highlight ? "shadow-md" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase font-semibold opacity-70 tracking-wider mb-0.5">
        {icon}
        {label}
      </div>
      <div className={`${highlight ? "text-xl" : "text-lg"} font-bold tabular-nums`}>{value}</div>
    </div>
  );
}
